import {
  RenderProofMetrics,
  RenderProofResponse,
} from './api/RenderProofResponse';
import {
  describeImprovement,
  formatByteSize,
  ImprovementDirection,
  ImprovementKind,
} from './RenderProofFormatting';

export type ComparisonTone = 'gain' | 'none';

export type ComparisonRow = {
  key: string;
  metric: string;
  before: string;
  after: string;
  improvement: string;
  tone: ComparisonTone;
};

export type RenderProofComparison = {
  /** False hides the table and its intro line entirely. */
  isVisible: boolean;
  rows: ComparisonRow[];
  /** False when there is no "before" data worth a column. */
  showBeforeColumn: boolean;
};

type MetricRowConfig = {
  key: string;
  label: string;
  kind: ImprovementKind;
  /** Defaults to `increase`; only page weight improves in both directions. */
  direction?: ImprovementDirection;
  value: (metrics: RenderProofMetrics) => number;
  format: (value: number) => string;
};

const IDENTITY_FORMAT = (value: number): string => `${value}`;

const METRIC_ROWS: MetricRowConfig[] = [
  {
    key: 'html-size',
    label: 'HTML size',
    kind: 'multiplier',
    direction: 'either',
    value: (metrics) => metrics.htmlSizeBytes,
    format: formatByteSize,
  },
  {
    key: 'links',
    label: 'Links',
    kind: 'delta',
    value: (metrics) => metrics.linksCount,
    format: IDENTITY_FORMAT,
  },
  {
    key: 'headings',
    label: 'Headings',
    kind: 'delta',
    value: (metrics) => metrics.headingsCount,
    format: IDENTITY_FORMAT,
  },
  {
    key: 'structured-data',
    label: 'Structured data',
    kind: 'delta',
    value: (metrics) => metrics.structuredDataCount,
    format: IDENTITY_FORMAT,
  },
  {
    key: 'open-graph',
    label: 'Open Graph tags',
    kind: 'delta',
    value: (metrics) => metrics.openGraphTagsCount,
    format: IDENTITY_FORMAT,
  },
];

/**
 * Decides what the comparison table shows, per the product rules:
 *
 * - a *count* that came out worse with Prerender is dropped, not shown red;
 * - page weight is kept whichever way it moved: more content or less payload
 *   are both improvements for a crawler;
 * - a metric that did not move stays, labelled "No change";
 * - the table (and its intro line) only appears when at least one metric
 *   genuinely improved — otherwise the screen is the summary alone;
 * - when there is no "before" data worth the name — the origin could not be
 *   fetched, or answered with nothing in it — the before and improvement
 *   columns go away rather than filling with dashes, and the table survives
 *   only if the rendered side has something to show.
 */
export function buildRenderProofComparison(
  proof: RenderProofResponse,
): RenderProofComparison {
  const { original, rendered } = proof;

  if (rendered === undefined) {
    return { isVisible: false, rows: [], showBeforeColumn: false };
  }

  // An origin that answered with an empty body is the same story as one we could
  // not fetch: there is nothing to compare against, so comparing is not what the
  // table does. Every "improvement" against zero is either a division by it or a
  // meaningless "+0".
  if (
    original === undefined ||
    !METRIC_ROWS.some((row) => row.value(original) > 0)
  ) {
    const rows = METRIC_ROWS.map((row) => ({
      key: row.key,
      metric: row.label,
      before: '',
      after: row.format(row.value(rendered)),
      improvement: '',
      tone: 'none' as ComparisonTone,
    }));

    const hasAnything = METRIC_ROWS.some((row) => row.value(rendered) > 0);
    return { isVisible: hasAnything, rows, showBeforeColumn: false };
  }

  const rows = METRIC_ROWS.flatMap((row) => {
    const beforeValue = row.value(original);
    const afterValue = row.value(rendered);

    const direction = row.direction ?? 'increase';

    // Worse with Prerender: the row is dropped rather than displayed as a loss.
    // Page weight has no worse direction — see `ImprovementDirection`.
    if (direction === 'increase' && afterValue < beforeValue) return [];

    const improvement = describeImprovement(
      beforeValue,
      afterValue,
      row.kind,
      direction,
    );

    return [
      {
        key: row.key,
        metric: row.label,
        before: row.format(beforeValue),
        after: row.format(afterValue),
        improvement: improvement.label,
        tone: (improvement.isGain ? 'gain' : 'none') as ComparisonTone,
      },
    ];
  });

  return {
    isVisible: rows.some((row) => row.tone === 'gain'),
    rows,
    showBeforeColumn: true,
  };
}
